import React, { useEffect, useRef } from 'react';
import { track, Events } from '../analytics';

const FAKE_DESCRIPTIONS = [
  'Daily updates, news, and community discussions. Join for exclusive posts and announcements.',
  'Curated content, tips, and resources shared regularly with active members.',
  'Behind-the-scenes updates, guides, and conversations you will not find anywhere else.',
  'A growing community sharing insights, links, and recommendations every day.',
  'Exclusive channels, timely alerts, and useful resources for subscribers.',
  'Regular posts covering trends, tools, and ideas from an active community.',
];

const styles = {
  container: { width: '100%', maxWidth: '860px', margin: '0 auto', padding: '0 16px 40px' },
  item: {
    display: 'flex', alignItems: 'center', gap: '16px', padding: '18px', marginBottom: '12px',
    border: '1px solid #e5e7eb', borderRadius: '14px', background: '#fff', cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(15,23,42,.05)',
  },
  image: { width: '58px', height: '58px', borderRadius: '50%', objectFit: 'cover', background: '#eef2f7', flexShrink: 0 },
  blurredImage: { filter: 'blur(6px)', transform: 'scale(1.08)' },
  content: { minWidth: 0, flex: 1 },
  titleRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  title: { fontSize: '17px', fontWeight: 650, color: '#202124', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  description: { marginTop: '7px', color: '#4b5563', lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  blurredDescription: { filter: 'blur(4px)', userSelect: 'none' },
  metadata: { marginTop: '8px', color: '#6b7280', fontSize: '13px' },
  loadMore: {
    width: '100%', marginTop: '8px', padding: '18px 20px', border: '1px solid #e5e7eb', borderRadius: '14px',
    background: '#fff', color: '#202124', fontSize: '17px', fontWeight: 650, cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(15,23,42,.05)',
  },
  message: { padding: '48px 16px', textAlign: 'center', color: '#6b7280' },
};

function fakeDescription(seed) {
  const hash = [...String(seed)].reduce((total, char) => total + char.charCodeAt(0), 0);
  return FAKE_DESCRIPTIONS[Math.abs(hash) % FAKE_DESCRIPTIONS.length];
}

function openResult(result, index) {
  track(Events.RESULT_CLICKED, { channel_id: result.id, position: index });
  window.open(`https://t.me/${result.id}`, '_blank', 'noopener,noreferrer');
}

export function SearchResults({
  results = [],
  isLoading,
  hasMore,
  totalResults,
  query = '',
  tier = 'unknown',
  onLocked,
  onUpgrade,
  strings,
}) {
  const impressionQuery = useRef('');
  const lockedCount = results.filter((result) => result.locked === true).length;
  const total = totalResults == null ? results.length : totalResults;

  useEffect(() => {
    if (isLoading) return;
    if (lockedCount === 0 && !hasMore) return;
    if (impressionQuery.current === query) return;
    impressionQuery.current = query;
    track(Events.PAYWALL_IMPRESSION, {
      locked_count: lockedCount,
      total_results: total,
      has_more: Boolean(hasMore),
      tier,
    });
  }, [query, lockedCount, hasMore, isLoading, total, tier]);

  if (isLoading) return <div style={styles.message}>{strings.loading || 'Loading...'}</div>;
  if (!results.length) return <div style={styles.message}>{strings.noResults || 'No results found'}</div>;

  return (
    <div style={styles.container}>
      {results.map((result, index) => {
        const locked = result.locked === true;
        const title = locked ? result.previewTitle : (result['og:title'] || strings.untitledChannel || 'Untitled channel');
        const imageStyle = locked ? { ...styles.image, ...styles.blurredImage } : styles.image;
        return (
          <div
            key={locked ? `locked-${index}` : result.id || index}
            style={styles.item}
            role="button"
            tabIndex={0}
            onClick={() => locked ? onLocked() : openResult(result, index)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                locked ? onLocked() : openResult(result, index);
              }
            }}
          >
            {result['og:image'] ? (
              <img
                src={result['og:image']}
                alt=""
                style={imageStyle}
                onError={(event) => { event.currentTarget.style.visibility = 'hidden'; }}
              />
            ) : (
              <div style={imageStyle} />
            )}
            <div style={styles.content}>
              <div style={styles.titleRow}>
                <div style={styles.title}>{title}</div>
              </div>
              <div style={locked ? { ...styles.description, ...styles.blurredDescription } : styles.description}>
                {locked
                  ? fakeDescription(result.previewTitle || index)
                  : (result['og:description'] || strings.noDescription)}
              </div>
              {result.subs !== undefined && result.subs !== '' && (
                <div style={styles.metadata}>👥 {result.subs} {strings.subscribers}</div>
              )}
            </div>
          </div>
        );
      })}
      {hasMore && (
        <button type="button" style={styles.loadMore} onClick={onUpgrade}>
          {strings.loadMore || 'Load more'}
        </button>
      )}
    </div>
  );
}
